import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  OrganizationChannel,
  ConfiguredChannel,
  DistributionList,
  Template,
  RenderedTemplate,
  Communication,
  EverbridgeTemplate,
  MassCommsContact,
  MassCommsContactDataset,
  EverbridgeReplaceTokensRequest,
  CommunicationRecipientActivity,
  CommunicationActivity,
  MassNotificationTemplate,
  MassNotificationTemplateGroup,
  MassNotification,
  MassNotificationRecipient,
  MassNotificationRecipientStats,
  MassNotificationRecipientCount,
  MassNotificationChannelStats,
  MassNotificationStats,
  MassNotificationRecipientResponsesResult,
  MassNotificationRecipientResponsesRequest,
  MassNotificationResponseOverTime,
  MassNotificationResponsesOverTimeRequest,
  MassNotificationRecipientsTransitionRequest,
  MassNotificationActivity,
  TeamGroupInfo,
  TeamsTestResult,
  MassNotificationRuleDetailsDto,
  DistributionListCount,
  ClosestAssetRefRelatedDocumentsRequest,
  RefRelatedDocumentsResponse,
  DocumentsRefRelatedDocumentsRequest,
  MassNotificationResponseCount,
  DefaultConfiguredChannelSettings,
  MassNotificationResponsesStats
} from './communications.model';
import { QueryParams, QueryParamsForSelection, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { GUID_EMPTY } from '@app/core/constants';
import { FilterGroup } from 'src/app/modules/core/store/models';
import { Document, DocumentSchema } from 'src/app/modules/core/store/models/document.model';
import { Dataset, DatasetsRequest } from 'src/app/modules/core/store/models/dataset.model';
import { WorkflowRuleDataField } from '../workflow/workflow.models';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class CommunicationsApiService {
  constructor(private http: HttpService) {}

  //
  // Channels
  //
  public getOrganizationChannels(params: QueryParams): Observable<QueryResult<OrganizationChannel>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/organizationChannels?${queryString}`;

    return this.http.get<QueryResult<OrganizationChannel>>(url);
  }

  public getOrganizationChannelUIdsOnly(): Observable<string[]> {
    const url = `${apiBaseUrl}/v1/notifications/organizationChannels/channel-uids-for-user-prefs`;

    return this.http.get<string[]>(url);
  }

  public saveConfiguredChannel(channel: ConfiguredChannel): Observable<ConfiguredChannel> {
    if (channel.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/configuredChannels`;
      return this.http.post(url, channel);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/configuredChannels/${channel.uId}`;
      return this.http.put(url, channel);
    }
  }

  public getConfiguredChannels(params: QueryParams): Observable<QueryResult<ConfiguredChannel>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/configuredchannels?${queryString}`;

    return this.http.get<QueryResult<ConfiguredChannel>>(url);
  }

  public deleteConfiguredChannel(channel: ConfiguredChannel): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}`;

    return this.http.delete(url);
  }

  public getSlackChannelsForConfiguredChannel(channel: ConfiguredChannel): Observable<any[]> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/slack/channels`;

    return this.http.get(url);
  }

  public disconnectChannelFromSlack(channel: ConfiguredChannel): Observable<ConfiguredChannel> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/slack/disconnect`;

    return this.http.post(url);
  }

  public createChannelAuthStateToken(channel: ConfiguredChannel): Observable<string> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/create-state-token`;

    return this.http.get(url, { responseType: 'text' });
  }

  public getChannelTeamGroups(channel: ConfiguredChannel): Observable<TeamGroupInfo[]> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/team-groups`;

    return this.http.get(url);
  }

  public syncChannelMembers(channelUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channelUId}/sync-members`;

    return this.http.post(url);
  }

  public testTeamsGraphApi(channelUId: string, emailAddress?: string): Observable<TeamsTestResult> {
    let url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channelUId}/test-teams-api`;
    if (!!emailAddress) {
      url = `${url}?emailAddress=${emailAddress}`;
    }
    return this.http.get(url);
  }

  public getDefaultConfiguredChannel(): Observable<DefaultConfiguredChannelSettings> {
    const url = `${apiBaseUrl}/v1/notifications/twilio/default-configured-channel`;
    return this.http.get<DefaultConfiguredChannelSettings>(url);
  }

  //
  // Distribution Lists
  //
  public getDistributionLists(params: QueryParams): Observable<QueryResult<DistributionList>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/distributionLists?${queryString}`;

    return this.http.get<QueryResult<DistributionList>>(url);
  }

  public getDistributionList(uId: string): Observable<DistributionList> {
    return this.http.get(`${apiBaseUrl}/v1/notifications/distributionLists/${uId}`);
  }

  public getDistributionListsForSelection(params: QueryParamsForSelection): Observable<QueryResult<DistributionList>> {
    const url = `${apiBaseUrl}/v1/notifications/distributionLists/for-selection`;
    return this.http.post<QueryResult<DistributionList>>(url, params);
  }

  public getDistributionListMembersCount(uIds: string[]): Observable<DistributionListCount[]> {
    const url = `${apiBaseUrl}/v1/notifications/distributionLists/members-count`;

    const request = {
      distributionListUIds: uIds
    };

    return this.http.post<DistributionListCount[]>(url, request);
  }

  public saveDistributionList(distributionList: DistributionList): Observable<DistributionList> {
    if (distributionList.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/distributionLists`;
      return this.http.post(url, distributionList);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/distributionLists/${distributionList.uId}`;
      return this.http.put(url, distributionList);
    }
  }

  public deleteDistributionList(distributionList: DistributionList): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/distributionLists/${distributionList.uId}`;

    return this.http.delete(url);
  }

  public getDocumentsWithFilter(datasetUId: string, queryParams: QueryParams, filterGroup: FilterGroup): Observable<QueryResult<Document>> {
    const queryString = this.http.queryStringFromObject({ includeCount: true });

    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query?${queryString}`;

    const req = {
      filterGroup: filterGroup,
      calcFields: null,
      fields: null,
      sort: queryParams.sort,
      where: queryParams.where,
      pagination: {
        skip: queryParams.skip,
        take: queryParams.take
      }
    };

    return this.http.post<QueryResult<Document>>(url, req);
  }

  public getDataset(datasetUId: string): Observable<Dataset> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}?includeWorkflow=true`;

    return this.http.get<Dataset>(url);
  }

  //
  // Communication Templates
  //
  public getTemplates(params: QueryParams): Observable<QueryResult<Template>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates?${queryString}`;

    return this.http.get<QueryResult<Template>>(url);
  }

  public saveTemplate(template: Template): Observable<Template> {
    if (template.uId === GUID_EMPTY) {
      const url = `${apiBaseUrl}/v1/notifications/communicationtemplates`;
      return this.http.post(url, template);
    } else {
      const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/${template.uId}`;
      return this.http.put(url, template);
    }
  }

  public copyTemplate(template: Template): Observable<Template> {
    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/copy`;
    return this.http.post(url, template);
  }

  public deleteTemplate(template: Template): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/communicationtemplates/${template.uId}`;

    return this.http.delete(url);
  }

  public previewTemplate(communication: Communication): Observable<RenderedTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/communications/preview`;

    return this.http.post(url, communication);
  }

  public getCommunication(communicationUId: string): Observable<Communication> {
    const url = `${apiBaseUrl}/v1/notifications/communications/${communicationUId}`;

    return this.http.get<Communication>(url);
  }

  public sendCommunication(communication: Communication): Observable<RenderedTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/communications/send`;

    return this.http.post(url, communication);
  }

  public getCommunicationActivityForDocument(documentUId: string): Observable<QueryResult<CommunicationActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/communications/communicationactivity/${documentUId}`;

    return this.http.get<QueryResult<CommunicationActivity>>(url);
  }

  public getCommunicationRecipientActivity(communicationUId: string): Observable<QueryResult<CommunicationRecipientActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/communications/${communicationUId}/recipient-activity`;

    return this.http.get<QueryResult<CommunicationRecipientActivity>>(url);
  }

  public getEverbridgeTemplatesForConfiguredChannel(channel: ConfiguredChannel): Observable<EverbridgeTemplate[]> {
    const url = `${apiBaseUrl}/v1/notifications/configuredchannels/${channel.uId}/everbridge/templates`;

    return this.http.get(url);
  }

  public replaceEverbridgeTokens(template: EverbridgeTemplate, document: Document): Observable<EverbridgeTemplate> {
    let request: EverbridgeReplaceTokensRequest = { templateDto: template, documentDto: document };

    const url = `${apiBaseUrl}/v1/notifications/everbridge/replace-tokens`;

    return this.http.post(url, request);
  }

  ///
  /// Contacts
  ///
  public getMassCommsContactsFromDatasets(
    contactDatasets: MassCommsContactDataset[],
    skip: number = 0,
    take: number = 10,
    nameContains: string = null
  ): Observable<QueryResult<MassCommsContact>> {
    const queryString = this.http.queryStringFromObject({ skip, take, nameContains });

    const url = `${apiBaseUrl}/v1/notifications/Contacts/from-dataset?${queryString}`;

    return this.http.post<QueryResult<MassCommsContact>>(url, contactDatasets);
  }

  //
  // Mass Notifications
  //
  public getMassNotifications(params: QueryParams): Observable<QueryResult<MassNotification>> {
    const request = {
      ...params
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/notifications/mass-notifications?${queryString}`;

    return this.http.get<QueryResult<MassNotification>>(url);
  }

  public getMassNotification(uId: string): Observable<MassNotification> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/${uId}`;

    return this.http.get<MassNotification>(url);
  }

  public createMassNotification(notification: MassNotification): Observable<MassNotification> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications`;
    return this.http.post(url, notification);
  }

  public updateMassNotification(notification: MassNotification): Observable<MassNotification> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications`;
    return this.http.put(url, notification);
  }

  public deleteMassNotification(uid: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/${uid}`;
    return this.http.delete(url);
  }

  public getMassNotificationRecipientDatasetUIds(uId: string): Observable<string[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/recipient_datasets/${uId}`;

    return this.http.get<MassNotification>(url);
  }

  public getMassNotificationRecipientCounts(uIds: string[]): Observable<MassNotificationRecipientCount[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/recipient_counts`;

    const request = {
      notificationUIds: uIds
    };

    return this.http.post<MassNotificationRecipientCount[]>(url, request);
  }

  public getMassNotificationRuleDetails(uIds: string[]): Observable<MassNotificationRuleDetailsDto[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/source_rule_details`;

    const request = {
      notificationUIds: uIds
    };

    return this.http.post<MassNotificationRuleDetailsDto[]>(url, request);
  }

  public getMassNotificationStats(notification: MassNotification): Observable<MassNotificationStats> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/stats/${notification.uId}`;

    return this.http.get<MassNotificationStats>(url);
  }

  public getMassNotificationResponsesStats(notification: MassNotification): Observable<MassNotificationResponsesStats> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/stats-responses/${notification.uId}`;

    return this.http.get<MassNotificationResponsesStats>(url);
  }

  public getMassNotificationResponses(
    request: MassNotificationRecipientResponsesRequest
  ): Observable<MassNotificationRecipientResponsesResult> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/responses`;

    return this.http.post<MassNotificationRecipientResponsesResult>(url, request);
  }

  public getMassNotificationResponsesOverTime(
    notification: MassNotification,
    startTime: any,
    endTime: any,
    intervalMinutes: number
  ): Observable<MassNotificationResponseOverTime[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/responses-over-time`;
    const request = {
      notificationUId: notification.uId,
      startTime: startTime,
      endTime: endTime,
      intervalMinutes: intervalMinutes
    } as MassNotificationResponsesOverTimeRequest;

    return this.http.post<MassNotificationResponseOverTime[]>(url, request);
  }

  public getMassNotificationRuleDataFields(): Observable<WorkflowRuleDataField[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/rule-fields`;

    return this.http.get<WorkflowRuleDataField[]>(url);
  }

  //
  // Mass Notification Recipients
  //

  public getMassNotificationRecipients(notificationUId: string, params: QueryParams): Observable<QueryResult<MassNotificationRecipient>> {
    if (!params) {
      params = {
        skip: 0,
        take: 10,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };
    }

    const request = {
      ...params,
      notificationUId: notificationUId
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients?${queryString}`;

    return this.http.get<QueryResult<MassNotificationRecipient>>(url);
  }

  public getMassNotificationRecipient(uId: string): Observable<MassNotificationRecipient> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients/${uId}`;

    return this.http.get<MassNotificationRecipient>(url);
  }

  public getMassNotificationRecipientStats(
    notificationUId: string,
    uIds: string[],
    includeFirstResponses: boolean,
    includeAllResponses: boolean
  ): Observable<MassNotificationRecipientStats[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients/stats`;
    const request = {
      notificationUId: notificationUId,
      recipientUIds: uIds,
      includeFirstResponses: includeFirstResponses,
      includeAllResponses: includeAllResponses
    };
    return this.http.post<MassNotificationRecipientStats[]>(url, request);
  }

  public updateMassNotificationRecipient(recipient: MassNotificationRecipient): Observable<MassNotificationRecipient> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients`;
    return this.http.put(url, recipient);
  }

  public transitionRecipientsWorkflow(request: MassNotificationRecipientsTransitionRequest): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients/transition-workflow`;
    return this.http.post(url, request);
  }

  public getMassNotificationRecipientRuleDataFields(): Observable<WorkflowRuleDataField[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-recipients/rule-fields`;

    return this.http.get<WorkflowRuleDataField[]>(url);
  }

  public getClosestAssetRefRelatedDocuments(
    datasetUId: string,
    request: ClosestAssetRefRelatedDocumentsRequest
  ): Observable<RefRelatedDocumentsResponse> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/closest-asset-ref-related`;
    return this.http.post(url, request);
  }

  public getDocumentsRefRelatedDocuments(
    datasetUId: string,
    request: DocumentsRefRelatedDocumentsRequest
  ): Observable<RefRelatedDocumentsResponse> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/ref-related`;
    return this.http.post(url, request);
  }

  //
  // MassNotification Templates
  //
  public getMassNotificationTemplates(
    params: QueryParams,
    includeCommTemplates: boolean,
    checkAgainstOrgBusinessUnits: boolean,
    orgBusinessUnitUId: string = null
  ): Observable<QueryResult<MassNotificationTemplate>> {
    const request = {
      ...params,
      includeTemplates: includeCommTemplates,
      checkAgainstOrgBusinessUnits: checkAgainstOrgBusinessUnits,
      orgBusinessUnitUId: orgBusinessUnitUId
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates?${queryString}`;

    return this.http.get<QueryResult<MassNotificationTemplate>>(url);
  }

  public getMassNotificationTemplate(uId: string): Observable<MassNotificationTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${uId}`;

    return this.http.get<MassNotificationTemplate>(url);
  }

  public getMassNotificationTemplateByCommunicationTemplateUId(uId: string): Observable<MassNotificationTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${uId}/communication`;

    return this.http.get<MassNotificationTemplate>(url);
  }

  public getMassNotificationTemplateUsageCount(uId: string): Observable<number> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${uId}/usage-count`;

    return this.http.get<number>(url);
  }

  public createMassNotificationTemplate(template: MassNotificationTemplate): Observable<MassNotificationTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates`;
    return this.http.post(url, template);
  }

  public updateMassNotificationTemplate(template: MassNotificationTemplate): Observable<MassNotificationTemplate> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates`;
    return this.http.put(url, template);
  }

  public deleteMassNotificationTemplate(uid: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${uid}`;
    return this.http.delete(url);
  }

  public getStaticTextTranslations(): Observable<any> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/static-text-translations`;
    return this.http.get(url);
  }

  //
  // MassNotification Template Groups
  //
  public getMassNotificationTemplateGroups(
    params: QueryParams,
  ): Observable<QueryResult<MassNotificationTemplateGroup>> {
    const request = {
      ...params,
    };

    const queryString = this.http.queryStringFromObject(request);

    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups?${queryString}`;

    return this.http.get<QueryResult<MassNotificationTemplateGroup>>(url);
  }

  public hasMassNotificationTemplateGroups(): Observable<boolean> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups/feature-active`;
    return this.http.get<boolean>(url);
  }

  public getMassNotificationTemplateGroup(uId: string): Observable<MassNotificationTemplateGroup> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups/${uId}`;

    return this.http.get<MassNotificationTemplateGroup>(url);
  }

  public getMassNotificationTemplateGroupUsageCount(uId: string): Observable<number> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups/${uId}/usage-count`;

    return this.http.get<number>(url);
  }

  public createMassNotificationTemplateGroup(templateGroup: MassNotificationTemplateGroup): Observable<MassNotificationTemplateGroup> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups`;
    return this.http.post(url, templateGroup);
  }

  public updateMassNotificationTemplateGroup(templateGroup: MassNotificationTemplateGroup): Observable<MassNotificationTemplateGroup> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups`;
    return this.http.put(url, templateGroup);
  }

  public deleteMassNotificationTemplateGroup(uid: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-template-groups/${uid}`;
    return this.http.delete(url);
  }


  // dataset
  public getDatasets(
    params: QueryParams,
    includeWorkflow?: boolean,
    skipChildren?: boolean,
    includeTranslations?: boolean
  ): Observable<QueryResult<Dataset>> {
    const datasetsRequest = {
      ...params,
      includeWorkflow: !!includeWorkflow,
      skipChildren: !!skipChildren,
      includeTranslations: !!includeTranslations
    } as DatasetsRequest;

    const url = `${apiBaseUrl}/v1/meta/datasets/query`;

    return this.http.post(url, datasetsRequest);
  }

  public getDatasetSchemaWithTranslations(datasetUId: string): Observable<DocumentSchema> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}/schema.translations`;

    return this.http.get<DocumentSchema>(url);
  }

  public getWhatsAppTemplateDetails(cultureCode: string): Observable<any> {
    const queryString = this.http.queryStringFromObject({
      cultureCode
    });

    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/whatsapp-templates?${queryString}`;

    return this.http.get<any>(url);
  }

  public getWhatsAppTemplateGroup(templateGroupUId: string): Observable<any> {
    const queryString = this.http.queryStringFromObject({
      templateGroupUId
    });

    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/whatsapp-template-group?${queryString}`;

    return this.http.get<any>(url);
  }

  public getMassNotificationActivity(documentUId: string): Observable<QueryResult<MassNotificationActivity>> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notifications/notification-activity/${documentUId}`;
    return this.http.get<QueryResult<MassNotificationActivity>>(url);
  }

  public getTemplateBusinessUnitUIds(templateUId: string): Observable<string[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${templateUId}/business-units`;

    return this.http.get<any>(url);
  }

  public setTemplateBusinessUnitUIds(templateUId: string, unitUIds: string[]): Observable<string[]> {
    const url = `${apiBaseUrl}/v1/notifications/mass-notification-templates/${templateUId}/business-units`;
    const request = {
      uIds: unitUIds
    };
    return this.http.post(url, request);
  }
}
